import { Button } from '@/components/Button/Button'
import { GeneratorCommit } from '@/components/Generator/GeneratorCommit/GeneratorCommit'
import { Loader } from '@/components/Loader/Loader'
import { Popup } from '@/components/Popup/Popup'
import { Select } from '@/components/Select/Select'
import { TextArea } from '@/components/TextArea/TextArea'
import { GeneratorFormContext } from '@/hooks/useGeneratorForm'
import CogSvg from '@/icons/cog.svg?react'
import PlusSvg from '@/icons/plus.svg?react'
import { useCommitsState } from '@/state/commits'
import { useGeneratorState } from '@/state/generator'
import { usePostsState } from '@/state/posts'
import { usePresetsState } from '@/state/presets'
import { useProjectsState } from '@/state/projects'
import { useRepoListState } from '@/state/repoList'
import { useToneState } from '@/state/tones'
import { useUserState } from '@/state/user'
import type { FunctionalComponent } from 'preact'
import { useContext, useMemo } from 'preact/hooks'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

export const GeneratorForm: FunctionalComponent<{
  onClose: () => void
  onAddTone: () => void
  onEditTone: () => void
  onAddProject: () => void
  onEditProject: () => void
  onEditPreset: () => void
  onAddPreset: () => void
  onSuccess: () => void
}> = props => {
  const quota = useUserState(s => s.quota)
  const minusQuota = useUserState(s => s.minusQuota)
  const form = useContext(GeneratorFormContext)!
  const generatePost = usePostsState(s => s.generate)
  const presetList = usePresetsState(s => s.list)
  const projectList = useProjectsState(s => s.list)
  const preset = usePresetsState(useShallow(s => s.list.find(item => item.id === form.presetId)))
  const repos = useRepoListState(s => s.list)
  const commits = useGeneratorState(s => s.list)
  const generating = usePostsState(s => s.generating)
  const setProcessed = useCommitsState(s => s.setProcessed)
  const clearGenerator = useGeneratorState(s => s.reset)
  const tonesList = useToneState(s => s.list)

  const submit = async () => {
    try {
      if (!form.presetId) throw new Error('Preset must be defined')
      if (!form.projectId) throw new Error('Project must be defined')
      if (!form.toneId) throw new Error('Tone of voice must be defined')

      const shas = commits
        .map(commit => {
          const repo = repos.find(repo => repo.id === commit.repoId)
          if (!repo) return null
          return {
            sha: commit.sha,
            owner: repo.owner.login,
            repo: repo.name,
          }
        })
        .filter(item => !!item)

      const result = await generatePost({
        toneId: form.toneId,
        presetId: form.presetId,
        projectId: form.projectId,
        context: form.context,
        shas: shas,
      })

      minusQuota()
      setProcessed(commits)
      clearGenerator()

      form.setPostId(result.post.id)
      form.setVariants(result.variants)
      props.onSuccess()
    }
    catch (e) {
      // TODO: Show notification
      console.error(e)
    }
  }

  const valid = useMemo(() => (
    (commits.length > 0 || form.context.length > 0)
    && (form.presetId !== null && form.projectId !== null && form.toneId !== null)
    && (quota && quota?.remaining > 0)
  ), [form.presetId, form.projectId, form.toneId, form.context, commits])

  return (
    <>
      <Popup.Title>
        Make post
      </Popup.Title>

      <Popup.Fields>
        <Popup.Field label='Project'>
          <div class={styles.line}>
            <Select
              disabled={generating}
              className={styles.select}
              placeholder='Select project'
              onChange={form.setProjectId}
              value={form.projectId}
              options={projectList.map(item => ({
                value: item.id,
                label: item.title,
                description: item.description ?? '',
              }))}
            />
            {form.projectId && (
              <Button
                shape='sqr'
                onClick={props.onEditProject}
                disabled={generating}
              >
                <CogSvg class={styles.icon} />
              </Button>
            )}
            <Button
              shape='sqr'
              onClick={props.onAddProject}
              disabled={generating}
            >
              <PlusSvg class={styles.icon} />
            </Button>
          </div>
        </Popup.Field>

        <Popup.Field label='Tone of voice'>
          <div class={styles.line}>
            <Select
              className={styles.select}
              disabled={generating}
              placeholder='Select tone of voice'
              onChange={form.setToneId}
              value={form.toneId}
              options={tonesList.map(tone => ({
                label: tone.name,
                value: tone.id,
              }))}
            />
            {form.toneId && (
              <Button
                shape='sqr'
                onClick={props.onEditTone}
                disabled={generating}
              >
                <CogSvg class={styles.icon} />
              </Button>
            )}
            <Button
              shape='sqr'
              onClick={props.onAddTone}
              disabled={generating}
            >
              <PlusSvg class={styles.icon} />
            </Button>
          </div>
        </Popup.Field>

        <Popup.Field label='Post type'>
          <div class={styles.line}>
            <Select
              className={styles.select}
              disabled={generating}
              placeholder='Select post type'
              value={form.presetId}
              onChange={form.setPresetId}
              options={presetList.map(item => ({
                value: item.id,
                label: item.name,
              }))}
            />
            {form.presetId && (
              <Button
                shape='sqr'
                onClick={props.onEditPreset}
                disabled={generating}
              >
                <CogSvg class={styles.icon} />
              </Button>
            )}
            <Button
              shape='sqr'
              onClick={props.onAddPreset}
              disabled={generating}
            >
              <PlusSvg class={styles.icon} />
            </Button>
          </div>
        </Popup.Field>

        {commits.length > 0
          ? (
            <Popup.Field label='Commits'>
              <div class={styles.list}>
                {commits.map(item => (
                  <GeneratorCommit
                    disabled={generating}
                    repoId={item.repoId}
                    sha={item.sha}
                  />
                ))}
              </div>
            </Popup.Field>
          )
          : (
            <Popup.Field label='Context'>
              <TextArea
                disabled={generating}
                value={form.context}
                onChange={form.setContext}
                placeholder={preset?.placeholder}
              />
            </Popup.Field>
          )}
      </Popup.Fields>

      <Popup.Footer>
        <Popup.Buttons />
        <Popup.Buttons>
          <Button
            onClick={props.onClose}
          >
            Back
          </Button>
          <Button
            type='primary'
            onClick={submit}
            disabled={generating || !valid}
          >
            Generate

            {generating && (
              <>
                {' '}
                <Loader />
              </>
            )}
          </Button>
        </Popup.Buttons>
      </Popup.Footer>
    </>
  )
}
